/**
 * FilesTransformResultsPage — превью и статистика ОДНОГО трансформированного
 * датасета. Сюда переехала старая FilesResultsPage (которая была
 * "/files/results?id=..."). Теперь это "/files/transform-results?id=...".
 *
 * - /files/results = список ВСЕХ датасетов проекта (новое поведение).
 * - /files/transform-results?id=... = превью одного датасета.
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { Eye, Loader2 } from 'lucide-react';

import { DataPreviewModal } from '@/components/files/DataPreviewModal';
import { useDataset } from '@/hooks/useFiles';
import { cn } from '@/lib/utils';

export function FilesTransformResultsPage() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const datasetId = searchParams.get('id');
  const [modalOpen, setModalOpen] = useState(false);

  const { data: dataset, isLoading: dsLoading } = useDataset(datasetId);

  if (!datasetId) {
    return (
      <div className="card text-sm text-surface-400 text-center py-8">
        {t('files.transform.no_dataset')}
      </div>
    );
  }

  if (dsLoading || !dataset) {
    return (
      <div className="text-sm text-surface-400 flex items-center gap-2">
        <Loader2 className="w-4 h-4 animate-spin" />
        {t('common.loading')}
      </div>
    );
  }

  const isBusy = dataset.status === 'uploaded' || dataset.status === 'parsing';

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold mb-1">
          {t('files.transform.title')}
        </h1>
        <p className="text-sm text-surface-400">
          {t('files.results.subtitle')} ·{' '}
          <span className="text-surface-300">{dataset.name}</span>
        </p>
      </div>

      <div className="card space-y-3">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div>
            <div className="text-lg font-medium">{dataset.original_filename}</div>
            <div className="text-xs text-surface-500 mt-1">
              {dataset.file_type.toUpperCase()} · {dataset.file_size_bytes} B
            </div>
          </div>
          <span
            className={cn(
              'text-xs px-2 py-1 rounded',
              dataset.status === 'ready' && 'bg-success-tint/20 text-success',
              dataset.status === 'failed' && 'bg-danger-tint/20 text-danger',
              (dataset.status === 'uploaded' || dataset.status === 'parsing') &&
                'bg-info-tint/20 text-info300',
            )}
          >
            {t(`files.status.${dataset.status}`)}
          </span>
        </div>

        {isBusy && (
          <div className="flex items-center gap-2 text-sm text-info300">
            <Loader2 className="w-4 h-4 animate-spin" />
            {t('files.status.parsing')}
          </div>
        )}

        {dataset.status === 'ready' && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Stat label={t('files.results.col_rows')} value={dataset.total_rows} />
            <Stat label={t('files.results.col_cols')} value={dataset.total_columns} />
            <Stat
              label={t('files.results.col_errors')}
              value={
                (dataset.raw_metadata as { error_count?: number })
                  ?.error_count ?? 0
              }
            />
            <Stat
              label={t('files.results.col_pdf_method')}
              value={
                (dataset.raw_metadata as { pdf_extraction_method?: string })
                  ?.pdf_extraction_method ?? '—'
              }
            />
          </div>
        )}

        {dataset.status === 'failed' && dataset.error_message && (
          <div className="text-sm text-danger bg-danger-tint/10 rounded p-3 border border-danger-tint/30">
            {dataset.error_message}
          </div>
        )}

        {dataset.status === 'ready' && (
          <button
            onClick={() => setModalOpen(true)}
            className="btn-primary inline-flex items-center gap-2"
          >
            <Eye className="w-4 h-4" />
            {t('files.results.open_preview')}
          </button>
        )}
      </div>

      {dataset.status === 'ready' && dataset.total_rows === 0 && (
        <div className="card text-sm text-surface-400 text-center py-4">
          {t('files.results.no_rows')}
        </div>
      )}

      <DataPreviewModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        datasetId={modalOpen ? datasetId : null}
      />
    </div>
  );
}

function Stat({ label, value }: { label: string; value: number | string }) {
  return (
    <div className="bg-panel rounded p-3">
      <div className="text-xs text-surface-500 uppercase tracking-wider">
        {label}
      </div>
      <div className="text-2xl font-semibold mt-1">{value}</div>
    </div>
  );
}
